import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Ticket, Search, Filter, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import BoxSeatLogo from '../components/BoxSeatLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.append('event_type', typeFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/events/upcoming?${params.toString()}`);
      setEvents(res.data);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, categoryFilter, search]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents();
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const formatTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  const eventTypes = ['sports', 'concert', 'theater', 'comedy', 'festival'];
  const categories = ['baseball', 'football', 'basketball', 'hockey', 'soccer', 'rock', 'pop', 'r&b'];

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-zinc-400 hover:text-white transition-colors"><ArrowLeft size={20} /></button>
            <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/venues')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase hidden md:block">Venues</button>
            <button onClick={() => navigate('/meetups')} className="text-sm font-semibold text-zinc-400 hover:text-white transition-colors tracking-wide uppercase hidden md:block">Meetups</button>
            <button onClick={() => navigate('/login')} className="px-5 py-2 text-sm font-bold uppercase tracking-wide text-black bg-primary rounded-md hover:bg-primary-hover transition-all glow-amber">Sign In</button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Upcoming</p>
          <h1 data-testid="events-page-title" className="font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase">
            Philadelphia <span className="text-primary">Events</span>
          </h1>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                data-testid="events-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events, teams..."
                className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/20 text-white rounded-md placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <button type="submit" className="px-6 py-3 bg-primary text-black font-bold uppercase text-sm rounded-md hover:bg-primary-hover transition-all">Search</button>
          </form>
          <div className="flex gap-2">
            <select
              data-testid="events-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md text-sm focus:ring-2 focus:ring-primary"
            >
              <option value="">All Types</option>
              {eventTypes.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
            <select
              data-testid="events-category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-3 bg-black/50 border border-white/20 text-white rounded-md text-sm focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((evt) => (
              <div key={evt.id} data-testid="event-card" className="bg-surface border border-white/10 rounded-xl overflow-hidden hover:-translate-y-1 transition-transform duration-300">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold tracking-[0.15em] uppercase text-primary">{evt.event_type}</span>
                    {evt.category && <span className="text-xs px-2 py-1 bg-white/5 border border-white/10 rounded-full">{evt.category}</span>}
                  </div>
                  <h3 className="font-heading text-xl font-bold uppercase mb-3">{evt.title}</h3>
                  {evt.description && <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{evt.description}</p>}
                  <div className="space-y-2 text-sm text-zinc-400">
                    <div className="flex items-center gap-2"><Calendar size={14} className="text-zinc-500" /> {formatDate(evt.date_time)} at {formatTime(evt.date_time)}</div>
                    <div className="flex items-center gap-2"><MapPin size={14} className="text-zinc-500" /> {evt.venue_name}</div>
                    {evt.price_range && <div className="flex items-center gap-2"><Ticket size={14} className="text-primary" /> <span className="text-primary font-semibold">{evt.price_range}</span></div>}
                  </div>
                  {evt.teams?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {evt.teams.map((t) => <span key={t} className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded-full">{t}</span>)}
                    </div>
                  )}
                  {evt.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {evt.tags.map((t) => <span key={t} className="text-xs text-zinc-600">#{t}</span>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <Calendar size={48} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="font-heading text-2xl font-bold uppercase mb-2">No Events Found</h3>
            <p className="text-zinc-500">Try adjusting your filters or check back soon.</p>
          </div>
        )}
      </main>
    </div>
  );
}
